/**
 * The public profiles attributed to a subject.
 *
 * **Attaching is the attribution and detaching rejects it.** There is no separate profile
 * status to set: a researcher saying "this LinkedIn is them" is the same act as attaching
 * the assertion that carries the URL, recorded with their name, the moment and their
 * reason. So this panel shows attached and — on request — the ones somebody rejected,
 * rather than inventing a vocabulary alongside the one that already exists.
 *
 * **Excluding a profile does not delete anything.** A source still published the page and
 * still said what it said; a person deciding it is not about their subject does not unmake
 * that. Only the attribution is withdrawn.
 *
 * The match verdict shown is the matcher's proposal, and its vocabulary stops short of
 * confirmed by design (ADR-0008). Only a person confirms, and they do it by attaching.
 */

import { useState, type ReactElement } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { request } from "@/lib/api";
import { Badge, EmptyState, ErrorState, Loading } from "@/components/ui";
import { useCan } from "@/hooks/usePermissions";

interface Profile {
  assertion_id: string;
  link_id: string;
  url: string;
  platform: string;
  title: string;
  result_type: string;
  source: string;
  observed_at: string;
  extraction_confidence: number;
  match_verdict: string;
  information_credibility: number;
  evidence_sha256: string;
  rationale: string;
  attached_at: string;
  detached_at: string | null;
  is_attached: boolean;
}

/**
 * The profiles panel.
 *
 * @param props - The subject to read.
 * @returns The panel.
 */
export function SubjectProfilesPanel({ subjectId }: { subjectId: string }): ReactElement {
  const canEdit = useCan("create_run_cases");
  const queryClient = useQueryClient();
  const [includeDetached, setIncludeDetached] = useState(false);

  const profiles = useQuery({
    queryKey: ["subject-profiles", subjectId, includeDetached],
    queryFn: () =>
      request<Profile[]>(
        `/research/subjects/${subjectId}/profiles?include_detached=${String(includeDetached)}`,
      ),
  });

  // The same endpoint the evidence panel uses, because a profile *is* an attached
  // assertion. A second detach route would be a second way to record the same decision.
  const detach = useMutation({
    mutationFn: (linkId: string) =>
      request<void>(`/research/subjects/${subjectId}/evidence/${linkId}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["subject-profiles", subjectId] });
      void queryClient.invalidateQueries({ queryKey: ["subject-evidence", subjectId] });
      void queryClient.invalidateQueries({ queryKey: ["research-subjects"] });
    },
  });

  if (profiles.isLoading) return <Loading label="Loading profiles" />;
  if (profiles.error != null) return <ErrorState error={profiles.error} />;

  const rows = profiles.data ?? [];

  return (
    <section className="space-y-3" aria-label="Profiles panel" data-testid="subject-panel-profiles">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="max-w-2xl text-xs text-muted">
          Pages a researcher attributed to this person. Attaching is the attribution;
          excluding one rejects it and leaves the page, the claim and the captured evidence
          exactly where they were.
        </p>
        <label className="flex items-center gap-2 text-xs text-muted">
          <input
            type="checkbox"
            data-testid="profiles-include-detached"
            checked={includeDetached}
            onChange={(event) => setIncludeDetached(event.target.checked)}
          />
          Show rejected
        </label>
      </div>

      {detach.error != null && <ErrorState error={detach.error} />}

      {rows.length === 0 ? (
        <EmptyState>
          No profiles attributed yet. Attach a profile page from a search result.
        </EmptyState>
      ) : (
        <ul className="space-y-2" data-testid="profile-list">
          {rows.map((profile) => (
            <li
              key={profile.link_id}
              className="space-y-1 rounded border border-line p-3"
              data-testid="profile-row"
            >
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={profile.is_attached ? "ok" : "muted"}>
                  {profile.is_attached ? "Attributed" : "Rejected"}
                </Badge>
                {profile.platform !== "" && <Badge tone="muted">{profile.platform}</Badge>}
                {profile.match_verdict !== "" && (
                  <span className="text-xs text-muted">
                    matcher: {profile.match_verdict.replaceAll("_", " ")}
                  </span>
                )}
                <span className="font-mono text-xs text-muted">{profile.source}</span>
                {canEdit && profile.is_attached && (
                  <button
                    type="button"
                    className="ml-auto text-xs text-muted hover:text-danger"
                    data-testid="profile-detach"
                    onClick={() => detach.mutate(profile.link_id)}
                  >
                    Not this person
                  </button>
                )}
              </div>
              <a
                className="block text-sm text-accent"
                href={profile.url}
                rel="noreferrer noopener"
                target="_blank"
              >
                {profile.title !== "" ? profile.title : profile.url}
              </a>
              {profile.rationale !== "" && (
                <p className="text-xs text-muted">{profile.rationale}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
